"use strict";
/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ResultifiedPromise = void 0;
/**
 * Custom Promise that resolves to a Result and has helper methods to work
 * with the Result directly without awaiting.
 *
 * @template ValueType - The type of the value in the result
 * @template ErrorType - The type of the error in the result
 */
class ResultifiedPromise extends Promise {
    /**
     * Extract the OK value from result promise.
     *
     * Calls {@link Result.getOk} to extract the value.
     *
     * @returns
     * A promise containing a value of type {@link ValueType} if the result is {@link Ok}, otherwise
     * returns `null`.
     */
    getOk() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.then((result) => {
                return result.getOk();
            });
        });
    }
    /** Extract the Err value from the result promise.
     *
     * Calls {@link Result.getErr} to extract the value.
     *
     * @returns
     * A promise containing an error of type {@link ErrorType} if the result is {@link Err}, otherwise
     * returns `null`.
     */
    getErr() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.then((result) => {
                return result.getErr();
            });
        });
    }
    /**
     * If the result is Ok, returns the value, otherwise returns the provided fallback value
     *
     * @param fallback - Fallback value to return if the result is Err
     */
    getOr(fallback) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.then((result) => result.getOr(fallback));
        });
    }
    /**
     * If the result is Ok, returns the value, otherwise generates a fallback value
     * using the provided function
     *
     * @param generateFallback - Function to generate a fallback value if the result is Err
     */
    getOrElse(generateFallback) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.then((result) => result.getOrElse(generateFallback));
        });
    }
    /**
     * Returns the value if the result is successful, otherwise throws the error.
     *
     * Calls {@link Result.getOrThrow} to extract the value.
     *
     * @returns Value contained in the Result if it is an instance of {@link Ok}
     *
     * @deprecated Prefer using errors as values
     */
    getOrThrow() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.then((result) => result.getOrThrow());
        });
    }
    /** Converts the Result into a tuple of [value, error] */
    toTuple() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.then((result) => result.toTuple());
        });
    }
    /**
     * If the result is Ok, transforms the value using the provided function and
     * returns a new ResultifiedPromise with the transformed value.
     *
     * @template UpdatedValue - The type of the updated value after transformation
     * @param valueTransform - Function to transform the value if the result is Ok
     */
    map(valueTransform) {
        const mappedResultPromise = this.then((result) => result.map(valueTransform));
        return new ResultifiedPromise((resolve, reject) => {
            mappedResultPromise.then(resolve).catch(reject);
        });
    }
    /**
     * If the result is Err, transforms the error using the provided function and
     * returns a new ResultifiedPromise with the transformed error.
     *
     * @template UpdatedError - The type of the updated error after transformation
     * @param errorTransform - Function to transform the error if the result is Err
     */
    mapErr(errorTransform) {
        const mappedResultPromise = this.then((result) => result.mapErr(errorTransform));
        return new ResultifiedPromise((resolve, reject) => {
            mappedResultPromise.then(resolve).catch(reject);
        });
    }
}
exports.ResultifiedPromise = ResultifiedPromise;
//# sourceMappingURL=Promise.js.map